import { LightningElement, api, wire } from "lwc";
import { t } from "tbme/localization";
import { locale } from "tbme/localState";

type DowntimeBannerVariables = {
  startDateLong?: string;
  startDateShort?: string;
  startDateLocalizedLong?: string;
  startDateLocalizedShort?: string;
  startDateLongDateOnly?: string;
  startDateShortDateOnly?: string;
  startDateLocalizedLongDateOnly?: string;
  startDateLocalizedShortDateOnly?: string;
  startDateLongString?: string;
  startDateShortString?: string;
  startDateLocalizedLongString?: string;
  startDateLocalizedShortString?: string;
  startDateLocalizedMonth?: string;
  startDateLocalizedDay?: string;
  startDateLocalizedYear?: string;
  startDateLocalizedHour?: string;
  startDateLocalizedMinute?: string;
  startDateLocalizedDayPeriod?: string;
  startDateLocalizedTimeZoneName?: string;
  endDateLong?: string;
  endDateShort?: string;
  endDateLocalizedLong?: string;
  endDateLocalizedShort?: string;
  endDateLongDateOnly?: string;
  endDateShortDateOnly?: string;
  endDateLocalizedLongDateOnly?: string;
  endDateLocalizedShortDateOnly?: string;
  endDateLongString?: string;
  endDateShortString?: string;
  endDateLocalizedLongString?: string;
  endDateLocalizedShortString?: string;
  endDateLocalizedMonth?: string;
  endDateLocalizedDay?: string;
  endDateLocalizedYear?: string;
  endDateLocalizedHour?: string;
  endDateLocalizedMinute?: string;
  endDateLocalizedDayPeriod?: string;
  endDateLocalizedTimeZoneName?: string;
};
export default class extends LightningElement {
    @api userId = "";
    private isClosed = false;
     // Date only regex YYYY-MM-DD
     private dateOnlyRegex = /^(\d{4})-(0[1-9]|1[0-2]|[1-9])-([1-9]|0[1-9]|[1-2]\d|3[0-1])$/;
    
    private close() {
        this.isClosed = true;
        this.markDismissed();
    }
  
    private get isVisible() {
      return !(this.isClosed || this.hasBeenDismissed() || !this.htmlContent);
    }
  
    private get htmlContent() {
        if(this.getFormattedDowntimeBannerText(locale())) {
          return this.getFormattedDowntimeBannerText(locale());
        } else if(this.downtimeBannerLabel) {
          return t(this.downtimeBannerLabel, this.downtimeBannerVariables);
        } else if(this.getFormattedDowntimeBannerText("en")) {
          return this.getFormattedDowntimeBannerText("en");
        } else {
          return "";
        }
    }

    private getFormattedDowntimeBannerText(locale: string = "en") {
      const formattedLocale = locale.toUpperCase().replace("-", "_");
      let text =  window?.sfdcBase?.exp?.[`FEATURE_PROFILE_BANNER_TEXT_${formattedLocale}`] || "";
  
      // Replace all placeholders using the downtimeBannerVariables directly
      Object.entries(this.downtimeBannerVariables).forEach(([key, value]) => {
        text = text.replace(`{{${key}}}`, value || "");
      });
  
      return text;
    }
    
    /**
     * 
     * @param {string} date  - ISO 8601 date string. You either pass only the date or the date with the time offset in PST or PDT. Expected formats YYYY-MM-DD or YYYY-MM-DDT00:hh:mm-08:00 or YYYY-MM-DDThh:mm-07:00. 
     * @returns {Date} -  If only date was passed, time will be set to 12:00 AM (start of the day) in the Salesforce headquarters timezone (America/Los_Angeles). 
     *                    If a complete date and time string was passed then it is used as is to construct the date (that's why PDT or PST offset should be used for consistency).
     */
    private constructDate(dateString: string) {
      let result;
      try {
        if(this.dateOnlyRegex.test(dateString)) {
          // format the expected date string: given date at 12:00 AM 
          const year = parseInt(dateString.match(this.dateOnlyRegex)?.[1] || "0");
          const month = parseInt(dateString.match(this.dateOnlyRegex)?.[2] || "0");
          const day = parseInt(dateString.match(this.dateOnlyRegex)?.[3] || "0");
          const expectedDateString = `${month}/${day}/${year}, 12:00 AM`;

          // Are we in PST or PDT? Let's try.
          const resultPST = new Date(dateString + "T00:00:00-08:00");
          const resultPDT = new Date(dateString + "T00:00:00-07:00");
      
          const formatter = new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
              timeZone: "America/Los_Angeles", // We always use Salesforce headquarters timezone for the date formatting
          });

          if (formatter.format(resultPST) == expectedDateString) {
              result = resultPST;
          } else if (formatter.format(resultPDT) == expectedDateString) {
              result = resultPDT;
          }
        } else {
            // If we have a complete date and time string, we can use it as is
            // For consistency, we expect the full date string include a PST or PDT offset.
            result = new Date(dateString);
        }
      } catch (error) {
          console.error("error construction downtime banner date", error);
      }
      return result;
    }

    private get downtimeBannerVariables(): DowntimeBannerVariables {
      // Date formatters
      const notLocalizedLongFormatter = this.getDateFormatter("en", "long");
      const notLocalizedShortFormatter = this.getDateFormatter("en", "numeric");
      const localizedLongFormatter = this.getDateFormatter(locale(), "long");
      const localizedShortFormatter = this.getDateFormatter(locale(), "numeric");
      const notLocalizedLongFormatterDateOnly = this.getDateFormatter("en", "long", true);
      const notLocalizedShortFormatterDateOnly = this.getDateFormatter("en", "numeric", true);
      const localizedLongFormatterDateOnly = this.getDateFormatter(locale(), "long", true);
      const localizedShortFormatterDateOnly = this.getDateFormatter(locale(), "numeric", true);

      let variables: DowntimeBannerVariables = {};

      // startDate
      if (this.startDate) {
        try {
          const startDate = this.constructDate(this.startDate);
          // Not localized start date strings
          variables.startDateLong = notLocalizedLongFormatter.format(startDate);
          variables.startDateShort = notLocalizedShortFormatter.format(startDate);
          // Localized start date strings
          variables.startDateLocalizedLong = localizedLongFormatter.format(startDate);
          variables.startDateLocalizedShort = localizedShortFormatter.format(startDate);
          // Not localized start date strings(date only)
          variables.startDateLongDateOnly = notLocalizedLongFormatterDateOnly.format(startDate);
          variables.startDateShortDateOnly = notLocalizedShortFormatterDateOnly.format(startDate);
          // Localized start date strings(date only)
          variables.startDateLocalizedLongDateOnly = localizedLongFormatterDateOnly.format(startDate);
          variables.startDateLocalizedShortDateOnly = localizedShortFormatterDateOnly.format(startDate);

          if(this.dateOnlyRegex.test(this.startDate)) {
            variables.startDateLongString = variables.startDateLongDateOnly;
            variables.startDateShortString = variables.startDateShortDateOnly;
            variables.startDateLocalizedLongString = variables.startDateLocalizedLongDateOnly;
            variables.startDateLocalizedShortString = variables.startDateLocalizedShortDateOnly;
          } else {
            variables.startDateLongString = variables.startDateLong;
            variables.startDateShortString = variables.startDateShort;
            variables.startDateLocalizedLongString = variables.startDateLocalizedLong;
            variables.startDateLocalizedShortString = variables.startDateLocalizedShort;
          }

          // Localized start date parts
          const parts = localizedShortFormatter.formatToParts(startDate);
          variables.startDateLocalizedMonth = parts.find(part => part.type === "month")?.value || "";
          variables.startDateLocalizedDay = parts.find(part => part.type === "day")?.value || "";
          variables.startDateLocalizedYear = parts.find(part => part.type === "year")?.value || "";
          variables.startDateLocalizedHour = parts.find(part => part.type === "hour")?.value || "";
          variables.startDateLocalizedMinute = parts.find(part => part.type === "minute")?.value || "";
          variables.startDateLocalizedDayPeriod = parts.find(part => part.type === "dayPeriod")?.value || "";
          variables.startDateLocalizedTimeZoneName = parts.find(part => part.type === "timeZoneName")?.value || "";
        } catch (error) {
          console.error("unable to parse downtime banner start date", error);
        }
      }

      // endDate
      if (this.endDate) {
        try {
          const endDate = this.constructDate(this.endDate);
          // Not localized end date strings
          variables.endDateLong = notLocalizedLongFormatter.format(endDate);
          variables.endDateShort = notLocalizedShortFormatter.format(endDate);
          // Localized end date strings
          variables.endDateLocalizedLong = localizedLongFormatter.format(endDate);
          variables.endDateLocalizedShort = localizedShortFormatter.format(endDate);
          // Not localized end date strings(date only)
          variables.endDateLongDateOnly = notLocalizedLongFormatterDateOnly.format(endDate);
          variables.endDateShortDateOnly = notLocalizedShortFormatterDateOnly.format(endDate);
          // Localized end date strings(date only)
          variables.endDateLocalizedLongDateOnly = localizedLongFormatterDateOnly.format(endDate);
          variables.endDateLocalizedShortDateOnly = localizedShortFormatterDateOnly.format(endDate);

          if(this.dateOnlyRegex.test(this.endDate)) {
            variables.endDateLongString = variables.endDateLongDateOnly;
            variables.endDateShortString = variables.endDateShortDateOnly;
            variables.endDateLocalizedLongString = variables.endDateLocalizedLongDateOnly;
            variables.endDateLocalizedShortString = variables.endDateLocalizedShortDateOnly;
          } else {
            variables.endDateLongString = variables.endDateLong;
            variables.endDateShortString = variables.endDateShort;
            variables.endDateLocalizedLongString = variables.endDateLocalizedLong;
            variables.endDateLocalizedShortString = variables.endDateLocalizedShort;
          }

          // Localized end date parts
          const parts = localizedShortFormatter.formatToParts(endDate);
          variables.endDateLocalizedMonth = parts.find(part => part.type === "month")?.value || "";
          variables.endDateLocalizedDay = parts.find(part => part.type === "day")?.value || "";
          variables.endDateLocalizedYear = parts.find(part => part.type === "year")?.value || "";
          variables.endDateLocalizedHour = parts.find(part => part.type === "hour")?.value || "";
          variables.endDateLocalizedMinute = parts.find(part => part.type === "minute")?.value || "";
          variables.endDateLocalizedDayPeriod = parts.find(part => part.type === "dayPeriod")?.value || "";
          variables.endDateLocalizedTimeZoneName = parts.find(part => part.type === "timeZoneName")?.value || "";
        } catch (error) {
          console.error("unable to parse downtime banner end date", error);
        }
      }

      return variables;
    }

    private getDateFormatter(locale: string = "en", month: "long" | "numeric" = "long", dateOnly: boolean = false) {
      try {
        const formatter = new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: month,
          day: "numeric",
          hour: dateOnly ? undefined : "numeric",
          minute: dateOnly ? undefined : "numeric",
          timeZoneName: dateOnly ? undefined : "short",
          hour12: true,
          timeZone: "America/Los_Angeles", // We always use Salesforce headquarters timezone for the date formatting
        });
        return formatter;
      } catch (error) {
        console.error("unable to get downtime banner date formatter. using default formatter.", error);
        return new Intl.DateTimeFormat();
      }
    }

    private get downtimeBannerLabel() {
      return window?.sfdcBase?.exp?.FEATURE_PROFILE_BANNER_LABEL;
    }

    private get startDate() {
      return window?.sfdcBase?.exp?.FEATURE_PROFILE_BANNER_START_DATE;
    }

    private get endDate() {
      return window?.sfdcBase?.exp?.FEATURE_PROFILE_BANNER_END_DATE;
    }
  
    private hasBeenDismissed() {
      return !!sessionStorage.getItem(this.getStorageKey());
    }
  
    private markDismissed() {
      sessionStorage.setItem(this.getStorageKey(), "1");
    }
  
    private getStorageKey() {
      return `tbme__downtime_banner_dismissed_${this.userId}`;
    }
}
